import React, { useState, useEffect, useRef } from "react";
import { ArrowRightLeft, Activity, AlertTriangle, Bug, ClipboardCheck, Clock3, ExternalLink, LogIn, LogOut, MessageSquare, ShieldAlert, User, X } from "lucide-react";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { DisplayDetail } from "../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import StickyHeader from "../../../components/ModuleCommon/StickyHeader/StickyHeader";
import { ExpandableHeader } from "../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import {
  fetchTrailerById,
  moveTrailer,
  createHostlerJob,
  fetchTrailerLocations,
  updateTrailerStatus,
  checkoutTrailer,
  saveCheckoutActuals,
  fetchYardSettings,
  fetchLocationChildren,
  fetchDocksForWarehouse,
  resolveYmsGridOfficeCode,
  recordTrailerInspection,
  fetchTrailerInspections,
  clearTrailerHold,
  recordPickupArrival,
  recordDropoffDeparture,
} from "./api";
import {
  extractTrailerLocationSlots,
  filterLocationSlotsByWarehouse,
  matchSlotForYardSelection,
} from "./yardLocationsUtils";
import { usePrivilege } from "../../../guard/usePrivilege";
import {
  sectionFieldGridClass,
  gridSelectClass,
  toolbarPrimaryButtonClass,
  toolbarSecondaryButtonClass,
  labelClass,
  FormField,
} from "./yardRecordSectionStyles";

// ─── Display mappings ─────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  "Loaded",
  "Empty",
  "Partially Empty",
  "Partially Loaded",
  "Unloaded",
];

const STATUS_DISPLAY = {
  LOADED:           "Loaded",
  EMPTY:            "Empty",
  PARTIALLY_EMPTY:  "Partially Empty",
  PARTIALLY_LOADED: "Partially Loaded",
  UNLOADED:         "Unloaded",
};

const LOAD_TYPE_DISPLAY = {
  DROP_TRAILER: "Drop Trailer",
  LIVE_LOAD:    "Live Load",
};

// No canonical weight/volume unit list exists anywhere in the app (shipment's own
// weightUnit/volumeUnit are plain free-text, defaulting to "LB" in the EDI writer) — this is a
// new, minimal list, not a reuse of an existing one.
const WEIGHT_UNIT_OPTIONS = ["LB", "KG"];
const VOLUME_UNIT_OPTIONS = ["CUFT", "CBM"];

function normalizeUnit(value, options) {
  const upper = String(value ?? "").trim().toUpperCase();
  return options.includes(upper) ? upper : options[0];
}

const SHIPMENT_TYPE_DISPLAY = {
  INBOUND:  "Inbound",
  OUTBOUND: "Outbound",
};

const GATE_STATUS_DISPLAY = {
  CHECKED_IN:  "Checked In",
  CHECKED_OUT: "Checked Out",
};

// Activity log event badges — mirrors ShipmentActivity's status-badge palette, keyed by
// trailer event type instead of shipment status.
const EVENT_TYPE_DISPLAY = {
  CHECK_IN: "Checked In",
  MOVE: "Moved",
  STATUS_CHANGE: "Condition Updated",
  LOADING_COMPLETE: "Loading Completed",
  UNLOADING_COMPLETE: "Unloading Completed",
  CHECK_OUT: "Checked Out",
};

const EVENT_TYPE_BADGE_CLASS = {
  CHECK_IN: "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200",
  MOVE: "bg-indigo-100 dark:bg-indigo-900/40 text-black dark:text-indigo-200",
  STATUS_CHANGE: "bg-amber-100 dark:bg-amber-900/40 text-black dark:text-amber-200",
  LOADING_COMPLETE: "bg-emerald-100 dark:bg-emerald-900/40 text-black dark:text-emerald-200",
  UNLOADING_COMPLETE: "bg-emerald-100 dark:bg-emerald-900/40 text-black dark:text-emerald-200",
  CHECK_OUT: "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
  DEFAULT: "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};

// Display value → API enum (used when submitting move / status update)
const STATUS_TO_API = {
  "Loaded":           "LOADED",
  "Empty":            "EMPTY",
  "Partially Empty":  "PARTIALLY_EMPTY",
  "Partially Loaded": "PARTIALLY_LOADED",
  "Unloaded":         "UNLOADED",
};

// ─── Styles specific to the Move modal (not part of the shared record-section pattern) ──
const selectClass =
  "text-fluid-sm text-gray-900 dark:text-acs-dark-text border border-gray-300 dark:border-acs-dark-border " +
  "rounded-md px-fluid-input-x py-fluid-input-y bg-white dark:bg-acs-dark-bg w-full box-border cursor-pointer " +
  "appearance-none pr-8 min-h-[36px] outline-none focus:ring-2 focus:ring-acs-primary-navy/30 dark:focus:ring-acs-primary-navy-dark/40";

const textAreaClass =
  "text-fluid-sm text-gray-900 dark:text-acs-dark-text border border-gray-300 dark:border-acs-dark-border " +
  "rounded-md px-fluid-input-x py-fluid-input-y bg-white dark:bg-acs-dark-bg w-full box-border resize-y " +
  "outline-none focus:ring-2 focus:ring-acs-primary-navy/30 dark:focus:ring-acs-primary-navy-dark/40";

const modalOverlayClass =
  "fixed inset-0 z-[999] bg-black bg-opacity-40 flex items-center justify-center px-fluid-4";

const modalCardClass = "bg-white dark:bg-acs-dark-surface rounded-lg shadow-xl font-roboto w-[440px]";

const modalHeaderClass =
  "flex items-center justify-between px-fluid-4 py-fluid-3 border-b border-gray-200 dark:border-acs-dark-border";

const modalTitleClass = "text-fluid-base font-semibold text-gray-900 dark:text-acs-dark-text";

const modalCloseButtonClass =
  "bg-transparent border-none cursor-pointer text-gray-400 dark:text-acs-dark-subtext hover:text-gray-600 dark:hover:text-acs-dark-text text-fluid-xl leading-none";

const modalFooterClass =
  "flex justify-end gap-fluid-2 px-fluid-4 py-fluid-3 border-t border-gray-200 dark:border-acs-dark-border";

const modalCancelButtonClass =
  "px-fluid-button-x py-fluid-button-y border border-gray-300 dark:border-acs-dark-border rounded-md " +
  "text-fluid-sm text-gray-700 dark:text-acs-dark-text bg-white dark:bg-acs-dark-surface " +
  "hover:bg-gray-100 dark:hover:bg-acs-dark-hover transition-none";

const primaryButtonClass =
  "px-fluid-button-x py-fluid-button-y rounded-md text-fluid-sm font-semibold text-white " +
  "bg-acs-primary-navy dark:bg-acs-primary-navy-dark hover:bg-[#1E2A74] transition-none " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

// ─── Inspection report constants ───────────────────────────────────────────────
const CHECKPOINT_OPTIONS = [
  { value: "PRE_LOAD", label: "Pre-Load" },
  { value: "POST_LOAD", label: "Post-Load" },
  { value: "PRE_UNLOAD", label: "Pre-Unload" },
  { value: "POST_UNLOAD", label: "Post-Unload" },
];

const OUTCOME_OPTIONS = [
  { value: "OK", label: "OK — no issues" },
  { value: "MINOR_ISSUE", label: "Minor Issue (e.g. flat tire) — notify carrier" },
  { value: "REJECTED", label: "Rejected — structural/safety problem" },
];

const REJECTION_CODE_OPTIONS = [
  { value: "HAZARDOUS_CONDITION", label: "Hazardous Condition (pest infestation, animal presence, biohazard)" },
  { value: "STRUCTURAL_DAMAGE", label: "Structural Damage" },
  { value: "SAFETY_HAZARD", label: "Safety Hazard" },
  { value: "BRAKE_MECHANICAL_FAILURE", label: "Brake / Mechanical Failure" },
  { value: "CONTAMINATION_DAMAGED_CARGO", label: "Contamination / Damaged Cargo Area" },
  { value: "DOOR_SEAL_FAILURE", label: "Door Seal Failure" },
  { value: "DOT_COMPLIANCE_VIOLATION", label: "DOT Compliance Violation" },
  { value: "OTHER", label: "Other" },
];

/** Codes that get the stronger "urgent" visual treatment — biohazard/pest/animal conditions read as more severe than a routine mechanical rejection. */
const HAZARDOUS_REJECTION_CODES = new Set(["HAZARDOUS_CONDITION"]);

const CHECKPOINT_DISPLAY = Object.fromEntries(CHECKPOINT_OPTIONS.map((o) => [o.value, o.label]));
const OUTCOME_DISPLAY = {
  OK: "OK",
  MINOR_ISSUE: "Minor Issue",
  REJECTED: "Rejected",
};
const REJECTION_CODE_DISPLAY = Object.fromEntries(REJECTION_CODE_OPTIONS.map((o) => [o.value, o.label]));

function formatDateTimeDisplay(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

const Chevron = () => (
  <span className="absolute right-fluid-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 dark:text-acs-dark-subtext text-fluid-xs">
    ▾
  </span>
);

const isHostlerJobLoadType = (loadType) => {
  const normalized = String(loadType ?? "").toUpperCase();
  return normalized.includes("DROP") || normalized.includes("LIVE");
};

// ─── Move Modal ───────────────────────────────────────────────────────────────
// Mirrors the Check-In form's Yard Information pattern: Section + Parking Bay (YMS's own
// hierarchy) on one path, or Warehouse + Dock (the Appointment module's dock list) on the
// other — mutually exclusive, exactly like check-in, instead of two flat bay/dock dropdowns
// sourced from a single mixed list.
const MoveTrailerModal = ({ trailer, warehouseRef, currentStatus, onClose, onSuccess }) => {
  // Drop Trailer and Live Load moves are Hostler work (LGX-1158): Save opens a job for a Hostler
  // to drive, the same as the Yard Overview spot panel, and the trailer relocates on completion.
  const createsHostlerJob = isHostlerJobLoadType(trailer?.loadType);
  // TrailerDetailDTO.locationId is the trailer's plant/facility id, resolved server-side and
  // explicitly commented "used by Move modal" on the backend -- no need to re-derive it via
  // fuzzy name-matching against warehouseRef (which names a specific child warehouse, e.g. "Roth
  // Warehouse", not the plant, and isn't reliably matchable client-side; that's what the earlier
  // resolveWarehouseContextForCheckIn-based attempt got wrong here for this exact reason).
  const plantLocationId = trailer?.locationId ?? null;
  const officeCode = resolveYmsGridOfficeCode();
  const isLoadingContext = false;

  const [dockWarehouseOptions, setDockWarehouseOptions] = useState([]);
  const [isLoadingDockWarehouses, setIsLoadingDockWarehouses] = useState(false);
  const [dockWarehouseName, setDockWarehouseName] = useState("");
  const [dockWarehouseRef, setDockWarehouseRef] = useState("");
  // The dock warehouse is pre-seeded below as a convenience, which must NOT count as "the user
  // chose the Dock path" — usesDockPath keys on this flag instead, otherwise the seed disables
  // the Section/Parking Bay selects the moment the modal opens and the Bay path is unreachable.
  const [dockWarehousePicked, setDockWarehousePicked] = useState(false);
  const warehousePrepopulatedRef = useRef(false);

  // Warehouse options for the Dock path — children of the plant. Once loaded, pre-select the
  // trailer's own current warehouse (by display name — the DTO doesn't expose its locationRef).
  useEffect(() => {
    if (!plantLocationId) { setDockWarehouseOptions([]); return undefined; }
    let cancelled = false;
    setIsLoadingDockWarehouses(true);
    fetchLocationChildren(plantLocationId)
      .then((children) => {
        if (cancelled) return;
        const options = (Array.isArray(children) ? children : [])
          .filter((loc) => String(loc?.locationType ?? "").toUpperCase() === "WAREHOUSE")
          .map((loc) => ({
            id: loc.id,
            name: String(loc.locationName ?? "").trim(),
            ref: loc.locationRef ?? loc.ref ?? "",
          }))
          .filter((wh) => wh.name);
        setDockWarehouseOptions(options);

        const seedName = String(warehouseRef ?? "").trim().toLowerCase();
        if (!warehousePrepopulatedRef.current && seedName) {
          const matched = options.find((wh) => wh.name.toLowerCase() === seedName);
          if (matched) {
            warehousePrepopulatedRef.current = true;
            setDockWarehouseName(matched.name);
            setDockWarehouseRef(matched.ref);
            // Same warehouse for the Bay path, normalized to the option's own casing so the
            // select renders a matching option rather than falling back to the placeholder.
            setBayWarehouseName((prev) => (prev ? matched.name : prev));
          }
        }
      })
      .catch(() => { if (!cancelled) setDockWarehouseOptions([]); })
      .finally(() => { if (!cancelled) setIsLoadingDockWarehouses(false); });
    return () => { cancelled = true; };
  }, [plantLocationId, warehouseRef]);

  const [yardSections, setYardSections] = useState([]);
  // Office that owns the bay-path warehouse, as resolved by yard-settings.
  const [yardSettingsOfficeCode, setYardSettingsOfficeCode] = useState("");
  const [isLoadingSections, setIsLoadingSections] = useState(false);
  const [sectionName, setSectionName] = useState("");
  const [bayName, setBayName] = useState("");
  // Defaults to the trailer's own current warehouse so existing behavior is unchanged, but is
  // freely selectable — bays are already warehouse-scoped via their section's warehouse_ref,
  // this just exposes the same warehouse choice the Dock path already has for the Bay path too.
  const [bayWarehouseName, setBayWarehouseName] = useState(warehouseRef || "");

  const [dockOptions, setDockOptions] = useState([]);
  const [isLoadingDocks, setIsLoadingDocks] = useState(false);
  const [dockName, setDockName] = useState("");

  const [notes, setNotes]         = useState("");
  const [sealVerificationNotes, setSealVerificationNotes] = useState("");
  const [sealDiscrepancy, setSealDiscrepancy] = useState("");
  const [isSaving, setIsSaving]   = useState(false);

  const usesBayPath  = Boolean(sectionName || bayName);
  const usesDockPath = Boolean((dockWarehousePicked && dockWarehouseName) || dockName);

  // Section + Parking Bay options for the currently-selected bay-path warehouse (defaults to
  // the trailer's own, but the user can pick a sibling warehouse under the same plant).
  useEffect(() => {
    if (!bayWarehouseName) { setYardSections([]); setYardSettingsOfficeCode(""); return undefined; }
    let cancelled = false;
    setIsLoadingSections(true);
    // No office-code override: GET /api/yard-settings resolves against a SINGLE office when one
    // code is sent (YardSettingsController), but falls back across every assigned office when
    // several are. The Yard Settings tab and My Yard both omit it, so narrowing to the active
    // grid office here was the one call site that could miss sections the others find.
    fetchYardSettings(bayWarehouseName)
      .then((data) => {
        if (cancelled) return;
        setYardSections(Array.isArray(data?.sections) ? data.sections : []);
        setYardSettingsOfficeCode(String(data?.officeCode ?? "").trim());
      })
      .catch(() => { if (!cancelled) { setYardSections([]); setYardSettingsOfficeCode(""); } })
      .finally(() => { if (!cancelled) setIsLoadingSections(false); });
    return () => { cancelled = true; };
  }, [bayWarehouseName]);

  // Docks for the selected warehouse — resolved by ref (not name) to avoid the exact ambiguity
  // check-in hit when a warehouse shares its name with its parent plant.
  useEffect(() => {
    const key = dockWarehouseRef || dockWarehouseName;
    if (!key) { setDockOptions([]); return undefined; }
    let cancelled = false;
    setIsLoadingDocks(true);
    fetchDocksForWarehouse(key)
      .then((data) => {
        if (cancelled) return;
        setDockOptions(
          (Array.isArray(data) ? data : [])
            .map((d) => String(d.dockName ?? d.name ?? "").trim())
            .filter(Boolean)
        );
      })
      .catch(() => { if (!cancelled) setDockOptions([]); })
      .finally(() => { if (!cancelled) setIsLoadingDocks(false); });
    return () => { cancelled = true; };
  }, [dockWarehouseRef, dockWarehouseName]);

  const bayOptions = yardSections.find((s) => s.name === sectionName)?.bayNames ?? [];

  const handleSubmit = async () => {
    if (!usesBayPath && !usesDockPath) {
      notify.error({ title: "Validation", message: "Select a section and parking bay, or a warehouse and dock." });
      return;
    }
    if (usesBayPath && (!sectionName || !bayName)) {
      notify.error({ title: "Validation", message: "Select both a section and a parking bay." });
      return;
    }
    if (usesDockPath && !dockName) {
      notify.error({ title: "Validation", message: "Select a dock." });
      return;
    }

    setIsSaving(true);
    try {
      if (createsHostlerJob) {
        // A job's destination is a name, resolved server-side at completion. "Section – Bay"
        // (en dash) is the backend's full bay label, so a bay name repeated across sections
        // still resolves to the one picked here.
        const destinationWarehouse = usesBayPath ? bayWarehouseName : dockWarehouseName;
        const note = [
          notes.trim(),
          sealVerificationNotes.trim() && `Seal verification: ${sealVerificationNotes.trim()}`,
          sealDiscrepancy.trim() && `Seal discrepancy: ${sealDiscrepancy.trim()}`,
        ].filter(Boolean).join("\n");
        await createHostlerJob({
          trailerId:      trailer.id,
          toSpotLabel:    usesBayPath ? `${sectionName} – ${bayName}` : dockName,
          toWarehouseRef: destinationWarehouse && destinationWarehouse !== warehouseRef ? destinationWarehouse : null,
          note,
        });
        notify.success({
          title: "Hostler job created",
          message: `Trailer ${trailer.trailerNumber} will move once a Hostler completes the job.`,
        });
        onSuccess();
        onClose();
        return;
      }

      let toBayId = null;
      if (usesBayPath) {
        // yard-settings already carries each section's bay ids, resolved against the office that
        // owns this warehouse. Re-resolving through /api/trailers/locations with the active grid
        // office missed whenever that office wasn't the warehouse's own (LGX-1158).
        const section = yardSections.find((s) => s.name === sectionName);
        const bayFromSettings = (Array.isArray(section?.bays) ? section.bays : [])
          .find((b) => String(b?.name ?? "").trim() === String(bayName).trim());
        let resolvedBayId = bayFromSettings?.id ?? null;
        if (resolvedBayId == null) {
          const lookupOffice = yardSettingsOfficeCode || officeCode;
          const data = await fetchTrailerLocations(lookupOffice ? [lookupOffice] : undefined);
          const slots = filterLocationSlotsByWarehouse(extractTrailerLocationSlots(data), bayWarehouseName);
          const match = matchSlotForYardSelection(slots, { sectionName, bayName, sectionId: section?.sectionId });
          resolvedBayId = match.currentBayId;
        }
        if (resolvedBayId == null) {
          notify.error({
            title: "Move Failed",
            message: "Could not resolve the selected parking bay. Refresh and try again.",
          });
          setIsSaving(false);
          return;
        }
        toBayId = resolvedBayId;
      }

      await moveTrailer({
        trailerId:   trailer.id,
        toBayId,
        toDockLabel: usesDockPath ? dockName : null,
        notes,
        status:     STATUS_TO_API[currentStatus] || null,
        sealVerificationNotes: sealVerificationNotes.trim() || null,
        sealDiscrepancy: sealDiscrepancy.trim() || null,
      });
      notify.success({ title: "Moved", message: `Trailer ${trailer.trailerNumber} moved successfully.` });
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Move failed:", err);
      // Surface the backend's reason: a dock move with no linked Appointment is refused with a
      // 409 the user can act on, which the generic message hid (LGX-1158).
      notify.error({
        title: "Move Failed",
        message:
          err?.response?.data?.message ||
          err?.response?.data?.content ||
          "Could not move the trailer. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isLoadingOpts = isLoadingContext || isLoadingSections;

  return (
    <div className={modalOverlayClass}>
      <div className={modalCardClass}>
        <div className={modalHeaderClass}>
          <p className={modalTitleClass}>Move Trailer — {trailer.trailerNumber}</p>
          <button onClick={onClose} className={modalCloseButtonClass}>×</button>
        </div>

        <div className="px-fluid-4 pt-fluid-3 pb-fluid-1">
          {isLoadingOpts && (
            <p className="text-fluid-xs text-gray-400 dark:text-acs-dark-subtext mb-fluid-2">
              Loading yard layout…
            </p>
          )}

          <div className="mb-fluid-3">
            <label className={labelClass}>
              Warehouse{" "}
              <span className="text-gray-400 dark:text-acs-dark-subtext font-normal normal-case">(for bay)</span>
            </label>
            <div className="relative">
              <select
                value={bayWarehouseName}
                onChange={(e) => {
                  setBayWarehouseName(e.target.value);
                  setSectionName("");
                  setBayName("");
                  if (e.target.value) { setDockWarehouseName(""); setDockWarehouseRef(""); setDockName(""); setDockWarehousePicked(false); }
                }}
                disabled={isLoadingOpts || isLoadingDockWarehouses || usesDockPath || !dockWarehouseOptions.length}
                className={`${selectClass} ${isLoadingOpts || usesDockPath ? "opacity-60" : ""}`}
              >
                <option value="">
                  {isLoadingDockWarehouses ? "Loading warehouses…" : dockWarehouseOptions.length ? "Select warehouse" : "No warehouses configured"}
                </option>
                {dockWarehouseOptions.map((wh) => (
                  <option key={wh.id} value={wh.name}>{wh.name}</option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <div className="mb-fluid-3">
            <label className={labelClass}>
              Section <span className="text-Delete-red">*</span>
            </label>
            <div className="relative">
              <select
                value={sectionName}
                onChange={(e) => {
                  setSectionName(e.target.value);
                  setBayName("");
                  if (e.target.value) { setDockWarehouseName(""); setDockWarehouseRef(""); setDockName(""); setDockWarehousePicked(false); }
                }}
                disabled={isLoadingOpts || usesDockPath}
                className={`${selectClass} ${isLoadingOpts || usesDockPath ? "opacity-60" : ""}`}
              >
                <option value="">{yardSections.length ? "Select section" : "No sections configured"}</option>
                {yardSections.map((s) => (
                  <option key={s.name} value={s.name}>{s.name}</option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <div className="mb-fluid-3">
            <label className={labelClass}>
              Parking Bay <span className="text-Delete-red">*</span>
            </label>
            <div className="relative">
              <select
                value={bayName}
                onChange={(e) => setBayName(e.target.value)}
                disabled={isLoadingOpts || usesDockPath || !sectionName || !bayOptions.length}
                className={`${selectClass} ${isLoadingOpts || usesDockPath ? "opacity-60" : ""}`}
              >
                <option value="">{bayOptions.length ? "Select bay" : "—"}</option>
                {bayOptions.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <p className="text-fluid-xs text-gray-400 dark:text-acs-dark-subtext mb-fluid-3">
            — or —
          </p>

          <div className="mb-fluid-3">
            <label className={labelClass}>
              Warehouse{" "}
              <span className="text-gray-400 dark:text-acs-dark-subtext font-normal normal-case">(for dock)</span>
            </label>
            <div className="relative">
              <select
                value={dockWarehouseName}
                onChange={(e) => {
                  const selected = dockWarehouseOptions.find((wh) => wh.name === e.target.value);
                  setDockWarehouseName(e.target.value);
                  setDockWarehouseRef(selected?.ref || "");
                  setDockName("");
                  setDockWarehousePicked(Boolean(e.target.value));
                  if (e.target.value) { setSectionName(""); setBayName(""); }
                }}
                disabled={isLoadingOpts || isLoadingDockWarehouses || usesBayPath || !dockWarehouseOptions.length}
                className={`${selectClass} ${isLoadingOpts || usesBayPath ? "opacity-60" : ""}`}
              >
                <option value="">
                  {isLoadingDockWarehouses ? "Loading warehouses…" : dockWarehouseOptions.length ? "Select warehouse" : "No warehouses configured"}
                </option>
                {dockWarehouseOptions.map((wh) => (
                  <option key={String(wh.id ?? wh.name)} value={wh.name}>{wh.name}</option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <div className="mb-fluid-3">
            <label className={labelClass}>
              Dock{" "}
              <span className="text-gray-400 dark:text-acs-dark-subtext font-normal normal-case">(optional)</span>
            </label>
            <div className="relative">
              <select
                value={dockName}
                onChange={(e) => setDockName(e.target.value)}
                disabled={isLoadingOpts || usesBayPath || isLoadingDocks || !dockWarehouseName || !dockOptions.length}
                className={`${selectClass} ${isLoadingOpts || usesBayPath ? "opacity-60" : ""}`}
              >
                <option value="">
                  {isLoadingDocks ? "Loading docks…" : dockOptions.length ? "Select a dock" : "No docks configured"}
                </option>
                {dockOptions.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <Chevron />
            </div>
          </div>

          <div className="mb-fluid-3">
            <label className={labelClass}>
              Seal verification notes{" "}
              <span className="text-gray-400 dark:text-acs-dark-subtext font-normal normal-case">(optional)</span>
            </label>
            <textarea
              value={sealVerificationNotes}
              onChange={(e) => setSealVerificationNotes(e.target.value)}
              placeholder="Notes on seal check during move…"
              rows={2}
              className={textAreaClass}
            />
          </div>
          <div className="mb-fluid-3">
            <label className={labelClass}>
              Seal discrepancy{" "}
              <span className="text-gray-400 dark:text-acs-dark-subtext font-normal normal-case">(optional)</span>
            </label>
            <textarea
              value={sealDiscrepancy}
              onChange={(e) => setSealDiscrepancy(e.target.value)}
              placeholder="If seal issue observed, describe here…"
              rows={2}
              className={textAreaClass}
            />
          </div>

          <div className="mb-fluid-3">
            <label className={labelClass}>
              Notes{" "}
              <span className="text-gray-400 dark:text-acs-dark-subtext font-normal normal-case">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes…"
              rows={3}
              className={textAreaClass}
            />
          </div>
        </div>

        <div className={modalFooterClass}>
          <button onClick={onClose} disabled={isSaving} className={modalCancelButtonClass}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={isSaving || isLoadingOpts} className={primaryButtonClass}>
            {createsHostlerJob
              ? (isSaving ? "Creating job…" : "Create Hostler Job")
              : (isSaving ? "Moving…" : "Move")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Record Inspection Modal ────────────────────────────────────────────────────
const RecordInspectionModal = ({ trailerId, onClose, onSuccess }) => {
  const [checkpointType, setCheckpointType] = useState("PRE_LOAD");
  const [outcome, setOutcome] = useState("OK");
  const [rejectionCode, setRejectionCode] = useState("");
  const [rejectionCodeOther, setRejectionCodeOther] = useState("");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const isRejected = outcome === "REJECTED";
  const isOtherCode = rejectionCode === "OTHER";
  const missingRejectionCode = isRejected && !rejectionCode;
  const missingOtherText = isRejected && isOtherCode && !rejectionCodeOther.trim();
  const missingPhoto = isRejected && !photo;
  const canSubmit = !missingRejectionCode && !missingOtherText && !missingPhoto;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSaving(true);
    try {
      await recordTrailerInspection(trailerId, {
        checkpointType,
        outcome,
        rejectionCode: isRejected ? rejectionCode : null,
        rejectionCodeOther: isRejected && isOtherCode ? rejectionCodeOther.trim() : null,
        notes: notes.trim() || null,
        photo,
      });
      notify.success({
        title: "Inspection recorded",
        message: isRejected
          ? "Trailer rejected and placed on hold. Carrier has been notified."
          : outcome === "MINOR_ISSUE"
            ? "Issue recorded. Carrier has been notified."
            : "Inspection recorded.",
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      notify.error({
        title: "Could not record inspection",
        message: err?.response?.data?.content || err?.response?.data?.message || "Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={modalOverlayClass}>
      <div className={modalCardClass}>
        <div className={modalHeaderClass}>
          <p className={modalTitleClass}>Record Inspection</p>
          <button onClick={onClose} className={modalCloseButtonClass}>×</button>
        </div>

        <div className="px-fluid-4 py-fluid-3 max-h-[70vh] overflow-y-auto">
          <div className="mb-fluid-3">
            <label className={labelClass}>Checkpoint</label>
            <select
              className={selectClass}
              value={checkpointType}
              onChange={(e) => setCheckpointType(e.target.value)}
            >
              {CHECKPOINT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="mb-fluid-3">
            <label className={labelClass}>Outcome</label>
            <select
              className={selectClass}
              value={outcome}
              onChange={(e) => {
                setOutcome(e.target.value);
                if (e.target.value !== "REJECTED") {
                  setRejectionCode("");
                  setRejectionCodeOther("");
                }
              }}
            >
              {OUTCOME_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {isRejected && (
            <>
              <div className="mb-fluid-3">
                <label className={labelClass}>
                  Rejection Code <span className="text-red-500">*</span>
                </label>
                <select
                  className={selectClass}
                  value={rejectionCode}
                  onChange={(e) => {
                    setRejectionCode(e.target.value);
                    setRejectionCodeOther("");
                  }}
                >
                  <option value="">Select a reason…</option>
                  {REJECTION_CODE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {isOtherCode && (
                <div className="mb-fluid-3">
                  <label className={labelClass}>
                    Describe the issue <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={selectClass}
                    value={rejectionCodeOther}
                    onChange={(e) => setRejectionCodeOther(e.target.value)}
                    placeholder="e.g. Broken landing gear"
                  />
                </div>
              )}

              <div className="mb-fluid-3">
                <label className={labelClass}>
                  Photo <span className="text-red-500">*</span>{" "}
                  <span className="text-gray-400 dark:text-acs-dark-subtext font-normal normal-case">(required for rejection)</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                  className="block w-full text-fluid-sm text-gray-700 dark:text-acs-dark-text"
                />
              </div>
            </>
          )}

          {!isRejected && (
            <div className="mb-fluid-3">
              <label className={labelClass}>
                Photo{" "}
                <span className="text-gray-400 dark:text-acs-dark-subtext font-normal normal-case">(optional)</span>
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                className="block w-full text-fluid-sm text-gray-700 dark:text-acs-dark-text"
              />
            </div>
          )}

          <div className="mb-fluid-1">
            <label className={labelClass}>
              Notes{" "}
              <span className="text-gray-400 dark:text-acs-dark-subtext font-normal normal-case">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes…"
              rows={3}
              className={textAreaClass}
            />
          </div>
        </div>

        <div className={modalFooterClass}>
          <button onClick={onClose} disabled={isSaving} className={modalCancelButtonClass}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={isSaving || !canSubmit} className={primaryButtonClass}>
            {isSaving ? "Saving…" : "Save Inspection"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Clear Hold Modal ───────────────────────────────────────────────────────────
const ClearHoldModal = ({ trailerId, onClose, onSuccess }) => {
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await clearTrailerHold(trailerId, note.trim() || null);
      notify.success({ title: "Hold cleared", message: "Trailer can move and check out again." });
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      notify.error({
        title: "Could not clear hold",
        message: err?.response?.data?.content || err?.response?.data?.message || "Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={modalOverlayClass}>
      <div className={modalCardClass}>
        <div className={modalHeaderClass}>
          <p className={modalTitleClass}>Clear Rejection Hold</p>
          <button onClick={onClose} className={modalCloseButtonClass}>×</button>
        </div>
        <div className="px-fluid-4 py-fluid-3">
          <p className="text-fluid-sm text-gray-500 dark:text-acs-dark-subtext mb-fluid-3">
            This trailer is on hold from a rejected inspection. Clearing the hold allows it to be
            moved or checked out again.
          </p>
          <label className={labelClass}>
            Note{" "}
            <span className="text-gray-400 dark:text-acs-dark-subtext font-normal normal-case">(optional)</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Structural damage repaired on site"
            rows={3}
            className={textAreaClass}
          />
        </div>
        <div className={modalFooterClass}>
          <button onClick={onClose} disabled={isSaving} className={modalCancelButtonClass}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={isSaving} className={primaryButtonClass}>
            {isSaving ? "Clearing…" : "Clear Hold"}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Formats a Date as the "yyyy-MM-ddTHH:mm" shape `<input type="datetime-local">` expects, using
 * the browser's own local wall-clock fields (never UTC) — this is a starting default the
 * dispatcher can edit, not a computed value, so no timezone conversion happens here at all.
 */
const toDatetimeLocalValue = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Confirms + optionally backdates a "Record Pickup Arrival" / "Record Drop-off Departure" gate
 * event. The dispatcher logging it isn't always standing at the gate when it happens, so this
 * defaults to "now" (the browser's local clock) but lets them pick an earlier moment instead.
 * The datetime-local value is sent to the backend as a plain wall-clock string — no Date math,
 * no UTC conversion — since the backend already resolves the facility's own local "now" the same
 * way; converting here would silently reintroduce the exact bug this modal exists to avoid.
 */
const RecordGateEventModal = ({ title, description, onClose, onConfirm }) => {
  const [value, setValue] = useState(() => toDatetimeLocalValue(new Date()));
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const occurredAt = value ? (value.length === 16 ? `${value}:00` : value) : null;
      await onConfirm(occurredAt);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={modalOverlayClass}>
      <div className={modalCardClass}>
        <div className={modalHeaderClass}>
          <p className={modalTitleClass}>{title}</p>
          <button onClick={onClose} className={modalCloseButtonClass}>×</button>
        </div>
        <div className="px-fluid-4 py-fluid-3">
          <p className="text-fluid-sm text-gray-500 dark:text-acs-dark-subtext mb-fluid-3">{description}</p>
          <label className={labelClass}>When did this happen?</label>
          <input
            type="datetime-local"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="text-fluid-sm text-gray-900 dark:text-acs-dark-text border border-gray-300 dark:border-acs-dark-border rounded-md px-fluid-input-x py-fluid-input-y bg-white dark:bg-acs-dark-bg w-full box-border outline-none focus:ring-2 focus:ring-acs-primary-navy/30 dark:focus:ring-acs-primary-navy-dark/40"
          />
        </div>
        <div className={modalFooterClass}>
          <button onClick={onClose} disabled={isSaving} className={modalCancelButtonClass}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={isSaving || !value} className={primaryButtonClass}>
            {isSaving ? "Saving…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * One shared page for looking at (and, in "checkout" mode, closing out) a trailer already in
 * the yard — same 5-section layout as the Check-In form (Core Shipment Details, Equipment,
 * Driver Information, Yard Information, Documents & History) so View Details and Check Out
 * read as the same screen pattern, not two different designs.
 */
const TrailerDetailView = ({ trailer: trailerFromGrid, onMoveSuccess, mode = "view", onClose, onCheckedOut, onOpenCheckout }) => {
  const isCheckout = mode === "checkout";

  const [detail, setDetail]              = useState(null);
  const [isLoading, setIsLoading]        = useState(false);
  const [status, setStatus]              = useState("");
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [pickupDriverName, setPickupDriverName] = useState("");
  const [pickupDriverLicenseNumber, setPickupDriverLicenseNumber] = useState("");
  const [pickupDriverEmail, setPickupDriverEmail] = useState("");
  const [pickupDriverPhone, setPickupDriverPhone] = useState("");
  const [pickupDriverLicenseState, setPickupDriverLicenseState] = useState("");
  const [pickupDriverLicenseExpiry, setPickupDriverLicenseExpiry] = useState("");
  const [submittingCheckout, setSubmittingCheckout] = useState(false);
  const [submittingActuals, setSubmittingActuals] = useState(false);
  // Actuals captured at checkout — deliberately left blank rather than pre-filled from the
  // shipment's planned weight/pallets, so gate staff have to type the real observed values.
  const [checkoutSealNumber, setCheckoutSealNumber] = useState("");
  const [actualWeight, setActualWeight] = useState("");
  const [actualVolume, setActualVolume] = useState("");
  const [actualPallets, setActualPallets] = useState("");
  const [actualWeightUnit, setActualWeightUnit] = useState("");
  const [actualVolumeUnit, setActualVolumeUnit] = useState("");
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showClearHoldModal, setShowClearHoldModal] = useState(false);
  const [showRecordGateEventModal, setShowRecordGateEventModal] = useState(null); // "PICKUP_ARRIVAL" | "DROPOFF_DEPARTURE" | null
  const [inspections, setInspections] = useState([]);
  const [isLoadingInspections, setIsLoadingInspections] = useState(false);

  const { hasPrivilege } = usePrivilege();
  const canMoveTrailer = hasPrivilege("YMS:MOVE_POST");
  const canCheckOutTrailer = hasPrivilege("YMS:CHECKOUT_PATCH");
  const canRecordInspection = hasPrivilege("YMS:MOVE_POST");
  const canClearHold = hasPrivilege("YMS:OVERRIDE_POST");

  const loadInspections = async (trailerId) => {
    setIsLoadingInspections(true);
    try {
      const data = await fetchTrailerInspections(trailerId);
      setInspections(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load inspections:", err);
    } finally {
      setIsLoadingInspections(false);
    }
  };

  useEffect(() => {
    if (!trailerFromGrid?.id) return;

    const load = async () => {
      setIsLoading(true);
      try {
        const data = await fetchTrailerById(trailerFromGrid.id);
        setDetail(data);
        setStatus(STATUS_DISPLAY[data.status] || data.status || "");
        loadInspections(trailerFromGrid.id);
        // Pre-fill from a previously-saved CHECK_OUT event (e.g. dispatch already ran "Complete
        // Loading" and a gate guard is now finalizing checkout) — driver identity is now a
        // history list, not a flat field, so find the most recent CHECK_OUT entry specifically.
        const savedCheckoutDriver = (data.driverHistory || []).find((h) => h.eventType === "CHECK_OUT");
        setPickupDriverName(savedCheckoutDriver?.driverName || "");
        setPickupDriverLicenseNumber(savedCheckoutDriver?.licenseNumber || "");
        setPickupDriverEmail(savedCheckoutDriver?.driverEmail || "");
        setPickupDriverPhone(savedCheckoutDriver?.driverPhone || "");
        setPickupDriverLicenseState(savedCheckoutDriver?.licenseState || "");
        setPickupDriverLicenseExpiry(savedCheckoutDriver?.licenseExpiry || "");
        setCheckoutSealNumber(data.sealNumber || "");
        setActualWeight(data.checkoutWeight != null ? String(data.checkoutWeight) : "");
        setActualVolume(data.checkoutVolume != null ? String(data.checkoutVolume) : "");
        setActualPallets(data.checkoutPallets != null ? String(data.checkoutPallets) : "");
        setActualWeightUnit(normalizeUnit(data.checkoutWeightUnit || data.weightUnit, WEIGHT_UNIT_OPTIONS));
        setActualVolumeUnit(normalizeUnit(data.checkoutVolumeUnit || data.volumeUnit, VOLUME_UNIT_OPTIONS));
      } catch (err) {
        console.error("Failed to load trailer detail:", err);
        notify.error({ title: "Error", message: "Failed to load trailer details." });
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [trailerFromGrid?.id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px]">
        <Loader
          color="text-blue-600 dark:text-acs-dark-text"
          size="h-5 w-5"
          message="Loading trailer details..."
        />
      </div>
    );
  }

  if (!trailerFromGrid?.id && trailerFromGrid?._fromCheckIn && trailerFromGrid?.trailerNumber) {
    return (
      <div className="p-fluid-4 max-w-[520px] font-roboto">
        <h2 className="text-fluid-lg font-bold text-gray-900 dark:text-acs-dark-text mb-fluid-2">
          {trailerFromGrid.trailerNumber}
        </h2>
        <p className="text-fluid-sm text-gray-500 dark:text-acs-dark-subtext leading-relaxed">
          This trailer was added from gate check-in and is not yet linked in the yard system.
          Open this trailer again after the record syncs, or use the Trailers list once it shows a
          server id.
        </p>
      </div>
    );
  }

  if (!detail) return null;

  const displayStatus = STATUS_DISPLAY[detail.status] || detail.status || "";
  const outbound = String(detail.shipmentType ?? "").trim().toUpperCase().startsWith("OUTBOUND");

  // Outbound/live-load checkout must capture the ACTUAL weight/volume/pallets — the shipment's
  // own totalWeight/totalVolume/numberOfPallets are the planned figures set before loading and
  // may not match — plus the seal applied after loading, before the gate agent can release it.
  const checkoutMissing = [];
  if (isCheckout && outbound) {
    if (!checkoutSealNumber.trim()) checkoutMissing.push("Seal number");
    if (!actualWeight.trim()) checkoutMissing.push("Actual weight");
    if (!actualVolume.trim()) checkoutMissing.push("Actual volume");
    if (!actualPallets.trim()) checkoutMissing.push("Actual pallets");
    // Outbound trailers are checked in Empty and loaded before they leave — checking one out
    // while it's still marked Empty means nothing was ever recorded as loaded onto it.
    if (detail.status === "EMPTY") checkoutMissing.push("Trailer condition (still Empty)");
  }

  const movable = detail.movable !== false && detail.movable !== "false";
  const moveBlockedReason = detail.moveBlockedReason || detail.move_blocked_reason || "";
  const latestRejection = inspections.find((i) => i.outcome === "REJECTED");
  const holdIsHazardous = Boolean(latestRejection && HAZARDOUS_REJECTION_CODES.has(latestRejection.rejectionCode));

  const handleMove = () => {
    if (!movable) {
      notify.error({
        title: "Move unavailable",
        message: moveBlockedReason || "This trailer cannot be moved right now.",
      });
      return;
    }
    setShowMoveModal(true);
  };

  const handleConfirmPickupArrival = async (occurredAt) => {
    try {
      const updated = await recordPickupArrival(detail.id, occurredAt);
      setDetail(updated);
      notify.success({ title: "Pickup arrival recorded", message: "Synced to the shipment's gate-in actual." });
    } catch (err) {
      console.error(err);
      notify.error({
        title: "Could not record pickup arrival",
        message: err?.response?.data?.content || err?.response?.data?.message || "Please try again.",
      });
    }
  };

  const handleConfirmDropoffDeparture = async (occurredAt) => {
    try {
      const updated = await recordDropoffDeparture(detail.id, occurredAt);
      setDetail(updated);
      notify.success({ title: "Drop-off departure recorded", message: "Synced to the shipment's gate-out actual." });
    } catch (err) {
      console.error(err);
      notify.error({
        title: "Could not record drop-off departure",
        message: err?.response?.data?.content || err?.response?.data?.message || "Please try again.",
      });
    }
  };

  const locationHistory = Array.isArray(detail.locationHistory)
    ? detail.locationHistory
    : Array.isArray(detail.location_history)
      ? detail.location_history
      : [];
  // Newest first for the Activity drawer timeline — locationHistory itself stays chronological
  // (oldest first) for the plain list in Documents & History.
  const activityEntries = [...locationHistory].reverse();

  const savedStatusDisplay = STATUS_DISPLAY[detail.status] || detail.status || "";
  const statusDirty = status !== savedStatusDisplay;

  const handleSaveStatus = async () => {
    setIsSavingStatus(true);
    try {
      const updated = await updateTrailerStatus(detail.id, STATUS_TO_API[status] || status);
      setDetail(updated);
      setStatus(STATUS_DISPLAY[updated.status] || updated.status || "");
      notify.success({ title: "Status updated", message: `Trailer status set to ${status}.` });
      // Updates this open tab's own state above, but the grid this tab was opened from is a
      // separate component with its own cached trailer list — without this it keeps showing the
      // pre-update Condition until a manual reload.
      onMoveSuccess?.();
    } catch (err) {
      console.error(err);
      notify.error({
        title: "Update failed",
        message: err?.response?.data?.message || "Could not update trailer status.",
      });
    } finally {
      setIsSavingStatus(false);
    }
  };

  // WHO — the drop-off driver is the CHECK_IN event specifically, not just "whichever driver
  // is most recent" (which could be the pickup driver after checkout) — driver identity now
  // lives in a history list (many drivers can handle the same trailer), not a flat field.
  const checkInDriverEntry = (detail.driverHistory || []).find((h) => h.eventType === "CHECK_IN");

  const handleUsePickupSameAsDropoff = () => {
    setPickupDriverName(checkInDriverEntry?.driverName || "");
    setPickupDriverLicenseNumber(checkInDriverEntry?.licenseNumber || "");
    setPickupDriverLicenseState(checkInDriverEntry?.licenseState || "");
    setPickupDriverLicenseExpiry(checkInDriverEntry?.licenseExpiry || "");
    setPickupDriverEmail(checkInDriverEntry?.driverEmail || "");
    setPickupDriverPhone(checkInDriverEntry?.driverPhone || "");
  };

  const buildCheckoutActualsPayload = () => ({
    driverName: pickupDriverName.trim(),
    driverLicenseNumber: pickupDriverLicenseNumber.trim(),
    driverEmail: pickupDriverEmail.trim() || undefined,
    driverPhone: pickupDriverPhone.trim() || undefined,
    driverLicenseState: pickupDriverLicenseState.trim() || undefined,
    driverLicenseExpiry: pickupDriverLicenseExpiry || undefined,
    sealNumber: checkoutSealNumber.trim() || undefined,
    checkoutWeight: actualWeight.trim() || undefined,
    checkoutVolume: actualVolume.trim() || undefined,
    checkoutPallets: actualPallets.trim() || undefined,
    checkoutWeightUnit: actualWeight.trim() ? actualWeightUnit : undefined,
    checkoutVolumeUnit: actualVolume.trim() ? actualVolumeUnit : undefined,
  });

  const handleSaveActuals = async () => {
    if (!detail?.id || checkoutMissing.length) return;
    setSubmittingActuals(true);
    try {
      const updated = await saveCheckoutActuals(detail.id, detail, buildCheckoutActualsPayload());
      setDetail(updated);
      notify.success({
        title: outbound ? "Loading completed" : "Unloading completed",
        message: `Saved for trailer ${detail.trailerNumber}. A gate guard can now complete the check-out.`,
      });
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      notify.error({
        title: "Save failed",
        message: data?.message || data?.content || "Could not save actuals.",
      });
    } finally {
      setSubmittingActuals(false);
    }
  };

  const handleCompleteCheckout = async () => {
    if (!detail?.id || checkoutMissing.length) return;
    setSubmittingCheckout(true);
    try {
      await checkoutTrailer(detail.id, detail, buildCheckoutActualsPayload());
      notify.success({ title: "Checked out", message: `Trailer ${detail.trailerNumber} checked out.` });
      onCheckedOut?.();
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      notify.error({
        title: "Check-out failed",
        message: data?.message || data?.content || "Could not complete check-out.",
      });
    } finally {
      setSubmittingCheckout(false);
    }
  };

  const headerInfoData = {
    "Shipment Number": { type: "display", value: detail.shipmentNumber },
    "Trailer Number": { type: "display", value: detail.trailerNumber },
    "Trailer Condition": { type: "display", value: displayStatus },
    "Gate Status": { type: "display", value: GATE_STATUS_DISPLAY[detail.gateStatus] || detail.gateStatus },
    "Dwell Hours": { type: "display", value: detail.dwellTime },
  };

  const headerButtons = isCheckout
    ? [
        { label: "Close", onClick: onClose, color: "gray" },
        ...(outbound
          ? [
              {
                label: submittingActuals ? "Saving…" : "Complete Loading",
                onClick: handleSaveActuals,
                color: "blue",
                disabled: submittingActuals || checkoutMissing.length > 0,
              },
            ]
          : []),
        {
          label: submittingCheckout ? "Submitting…" : "Complete Check Out",
          onClick: handleCompleteCheckout,
          type: "primary",
          color: "blue",
          disabled: submittingCheckout || checkoutMissing.length > 0,
        },
      ]
    : [
        ...(detail.gateStatus === "CHECKED_IN" && onOpenCheckout && canCheckOutTrailer
          ? [
              {
                label: "Check Out",
                onClick: () => onOpenCheckout(detail),
                type: "primary",
                color: "blue",
                icon: <LogOut size={16} />,
              },
            ]
          : []),
        ...(canMoveTrailer
          ? [
              {
                label: "Move",
                onClick: handleMove,
                color: "blue",
                icon: <ArrowRightLeft size={16} />,
              },
            ]
          : []),
        ...(canRecordInspection
          ? [
              {
                label: "Action",
                type: "dropdown",
                color: "red",
                options: [
                  {
                    label: "Inspect",
                    icon: <ClipboardCheck size={16} className="text-gray-600" />,
                    onClick: () => setShowInspectionModal(true),
                  },
                  ...(detail.loadType === "DROP_TRAILER"
                      && detail.shipmentType === "OUTBOUND"
                      && detail.gateStatus === "CHECKED_IN"
                      && !detail.pickupArrivedAt
                    ? [{
                        label: "Record Pickup Arrival",
                        icon: <LogIn size={16} className="text-gray-600" />,
                        onClick: () => setShowRecordGateEventModal("PICKUP_ARRIVAL"),
                      }]
                    : []),
                  ...(detail.loadType === "DROP_TRAILER"
                      && detail.shipmentType === "INBOUND"
                      && detail.gateStatus === "CHECKED_IN"
                      && !detail.dropoffDepartedAt
                    ? [{
                        label: "Record Drop-off Departure",
                        icon: <LogOut size={16} className="text-gray-600" />,
                        onClick: () => setShowRecordGateEventModal("DROPOFF_DEPARTURE"),
                      }]
                    : []),
                ],
              },
            ]
          : []),
      ];

  return (
    <div className="w-full h-full min-h-0 overflow-y-auto font-roboto">
      <div className="sticky top-0 z-10 bg-white dark:bg-acs-dark-surface shadow-md w-full">
        <StickyHeader
          leftLabel={detail.trailerNumber}
          infoData={headerInfoData}
          onInfoChange={() => {}}
          mode={null}
          templates={[]}
          onSelectTemplate={() => {}}
          buttons={headerButtons}
        />
      </div>

      <div className="p-fluid-4">

      {!isCheckout && (
        <div className="flex items-center justify-between mb-fluid-2">
          <h3 className="text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
            Trailer Information
          </h3>
          <div className="flex items-center gap-fluid-1">
            <button
              type="button"
              onClick={() => setShowActivityPanel(true)}
              className={toolbarPrimaryButtonClass}
            >
              <Activity size={16} />
              Activity
            </button>
          </div>
        </div>
      )}

      {!isCheckout && detail.status === "REJECTED" && (
        <div
          className={`mb-fluid-3 flex items-center justify-between gap-fluid-2 rounded-md border px-fluid-3 py-fluid-2 ${
            holdIsHazardous
              ? "border-orange-400 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 ring-2 ring-orange-300 dark:ring-orange-800"
              : "border-red-300 dark:border-red-900/50 bg-red-50 dark:bg-red-900/10"
          }`}
        >
          <div className={`flex items-center gap-fluid-1.5 ${holdIsHazardous ? "text-orange-800 dark:text-orange-300" : "text-red-700 dark:text-red-400"}`}>
            {holdIsHazardous ? <Bug size={18} /> : <ShieldAlert size={18} />}
            <span className="text-fluid-sm font-semibold">
              {holdIsHazardous
                ? "HAZARDOUS CONDITION — this trailer was rejected for a pest/animal/biohazard finding and is on hold. Handle with care."
                : "This trailer was rejected during inspection and is on hold — moves and checkout are blocked."}
            </span>
          </div>
          {canClearHold && (
            <button
              type="button"
              onClick={() => setShowClearHoldModal(true)}
              className={toolbarSecondaryButtonClass}
            >
              Clear Hold
            </button>
          )}
        </div>
      )}

      {/* Section 1 — Core Shipment Details */}
      <ExpandableHeader headerSection="Core Shipment Details" headerType="primary">
        <div className={sectionFieldGridClass}>
          <DisplayDetail label="Shipment Number" value={detail.shipmentNumber} />
          <DisplayDetail label="SO Number" value={detail.soNumber} />
          <DisplayDetail label="PO Number" value={detail.poNumber} />
          <DisplayDetail label="Movement Type" value={SHIPMENT_TYPE_DISPLAY[detail.shipmentType] || detail.shipmentType} />
        </div>

        <div className={sectionFieldGridClass}>
          <DisplayDetail
            label="Planned Weight"
            value={[detail.totalWeight, detail.weightUnit].filter(Boolean).join(" ")}
          />
          <DisplayDetail
            label="Actual Weight"
            value={actualWeight}
            isEditable={isCheckout && outbound}
            onChange={isCheckout && outbound ? (v) => setActualWeight(v) : undefined}
            highlighted={isCheckout && outbound && !actualWeight.trim()}
          />
          <FormField label="Weight Unit">
            <select
              className={gridSelectClass}
              value={actualWeightUnit}
              onChange={(e) => setActualWeightUnit(e.target.value)}
              disabled={!(isCheckout && outbound)}
            >
              {WEIGHT_UNIT_OPTIONS.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </FormField>
          <DisplayDetail
            label="Planned Volume"
            value={[detail.totalVolume, detail.volumeUnit].filter(Boolean).join(" ")}
          />
          <DisplayDetail
            label="Actual Volume"
            value={actualVolume}
            isEditable={isCheckout && outbound}
            onChange={isCheckout && outbound ? (v) => setActualVolume(v) : undefined}
            highlighted={isCheckout && outbound && !actualVolume.trim()}
          />
          <FormField label="Volume Unit">
            <select
              className={gridSelectClass}
              value={actualVolumeUnit}
              onChange={(e) => setActualVolumeUnit(e.target.value)}
              disabled={!(isCheckout && outbound)}
            >
              {VOLUME_UNIT_OPTIONS.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </FormField>
          <DisplayDetail label="Planned Pallets" value={detail.numberOfPallets} />
          <DisplayDetail
            label="Actual Pallets"
            value={actualPallets}
            isEditable={isCheckout && outbound}
            onChange={isCheckout && outbound ? (v) => setActualPallets(v) : undefined}
            highlighted={isCheckout && outbound && !actualPallets.trim()}
          />
        </div>

        <div className={sectionFieldGridClass}>
          <DisplayDetail label="Origin" value={detail.originName || detail.portOfOrigin} />
          <DisplayDetail label="Origin Address Line 1" value={detail.originAddressLine1} />
          <DisplayDetail label="Origin Address Line 2" value={detail.originAddressLine2} />
          <DisplayDetail label="Origin City" value={detail.originCity} />
          <DisplayDetail label="Origin State" value={detail.originState} />
          <DisplayDetail label="Origin Zip Code" value={detail.originZip} />
        </div>

        <div className={sectionFieldGridClass}>
          <DisplayDetail label="Destination" value={detail.destinationName || detail.portOfDestination} />
          <DisplayDetail label="Destination Address Line 1" value={detail.destinationAddressLine1} />
          <DisplayDetail label="Destination Address Line 2" value={detail.destinationAddressLine2} />
          <DisplayDetail label="Destination City" value={detail.destinationCity} />
          <DisplayDetail label="Destination State" value={detail.destinationState} />
          <DisplayDetail label="Destination Zip Code" value={detail.destinationZip} />
        </div>

        <div className={sectionFieldGridClass}>
          <DisplayDetail
            label="Carrier"
            value={[detail.carrierOfficeCode, detail.carrierName].filter(Boolean).join(" - ")}
          />
          <DisplayDetail label="Carrier Address Line 1" value={detail.carrierAddressLine1} />
          <DisplayDetail label="Carrier City" value={detail.carrierCity} />
          <DisplayDetail label="Carrier State" value={detail.carrierState} />
          <DisplayDetail label="Carrier Zip Code" value={detail.carrierZip} />
        </div>
      </ExpandableHeader>

      {/* Section 2 — Equipment (Condition stays a live yard action, not just a field) */}
      <ExpandableHeader headerSection={`Equipment - ${displayStatus}`} headerType="primary">
        <div className={sectionFieldGridClass}>
          <DisplayDetail label="Equipment Type" value={detail.equipmentType} />
          <DisplayDetail label="Equipment Number" value={detail.trailerNumber} />
          <DisplayDetail label="License Plate" value={detail.licensePlate} />
          <DisplayDetail label="License State" value={detail.licenseState} />
          <FormField
            label="Condition"
            highlighted={isCheckout && outbound && detail.status === "EMPTY"}
          >
            <select
              className={gridSelectClass}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </FormField>
          <DisplayDetail label="Load Type" value={LOAD_TYPE_DISPLAY[detail.loadType] || detail.loadType} />
          <DisplayDetail label="Tare Weight" value={detail.tareWeight} />
          <DisplayDetail
            label="Seal Number"
            value={checkoutSealNumber}
            isEditable={isCheckout && outbound}
            onChange={isCheckout && outbound ? (v) => setCheckoutSealNumber(v) : undefined}
            highlighted={isCheckout && outbound && !checkoutSealNumber.trim()}
          />
        </div>
        {statusDirty && (
          <div className="px-fluid-3 pb-fluid-3 bg-[#F9FAFE] dark:bg-acs-dark-surface -mt-fluid-2">
            <button
              type="button"
              onClick={handleSaveStatus}
              disabled={isSavingStatus}
              className={toolbarPrimaryButtonClass}
            >
              {isSavingStatus ? "Saving…" : "Save Condition"}
            </button>
          </div>
        )}
      </ExpandableHeader>

      {/* Section 3 — Driver Information */}
      <ExpandableHeader headerSection="Driver Information" headerType="primary">
        <div className={sectionFieldGridClass}>
          <DisplayDetail label="Drop-off Driver" value={checkInDriverEntry?.driverName} />
          <DisplayDetail label="Drop-off Driver License #" value={checkInDriverEntry?.licenseNumber} />
          <DisplayDetail label="Driver License State" value={checkInDriverEntry?.licenseState} />
          <DisplayDetail label="Expiry Date" value={checkInDriverEntry?.licenseExpiry} />
          <DisplayDetail label="Drop-off Driver Email" value={checkInDriverEntry?.driverEmail} />
          <DisplayDetail label="Drop-off Driver Phone" value={checkInDriverEntry?.driverPhone} />

          {isCheckout && (
            <div className="col-span-6 -mb-fluid-1">
              <button
                type="button"
                onClick={handleUsePickupSameAsDropoff}
                className={toolbarSecondaryButtonClass}
              >
                Same as drop-off driver
              </button>
            </div>
          )}

          <DisplayDetail
            label="Pickup Driver"
            value={pickupDriverName}
            isEditable={isCheckout}
            onChange={isCheckout ? (v) => setPickupDriverName(v) : undefined}
          />
          <DisplayDetail
            label="Pickup Driver License #"
            value={pickupDriverLicenseNumber}
            isEditable={isCheckout}
            onChange={isCheckout ? (v) => setPickupDriverLicenseNumber(v) : undefined}
          />
          <DisplayDetail
            label="Pickup Driver License State"
            value={pickupDriverLicenseState}
            isEditable={isCheckout}
            onChange={isCheckout ? (v) => setPickupDriverLicenseState(v.slice(0, 2).toUpperCase()) : undefined}
          />
          <DisplayDetail
            label="Pickup Driver Expiry Date"
            value={pickupDriverLicenseExpiry}
            isEditable={isCheckout}
            inputType="date"
            showCalendar
            onChange={isCheckout ? (v) => setPickupDriverLicenseExpiry(v) : undefined}
          />
          <DisplayDetail
            label="Pickup Driver Email"
            value={pickupDriverEmail}
            isEditable={isCheckout}
            inputType="email"
            onChange={isCheckout ? (v) => setPickupDriverEmail(v) : undefined}
          />
          <DisplayDetail
            label="Pickup Driver Phone (with country code)"
            value={pickupDriverPhone}
            isEditable={isCheckout}
            inputType="tel"
            onChange={isCheckout ? (v) => setPickupDriverPhone(v) : undefined}
          />
        </div>
      </ExpandableHeader>

      {/* Section 4 — Yard Information */}
      <ExpandableHeader headerSection="Yard Information" headerType="primary">
        <div className={sectionFieldGridClass}>
          <DisplayDetail label="Location" value={detail.locationName} />
          <DisplayDetail label="Section" value={detail.sectionName} />
          <DisplayDetail label="Parking Bay" value={detail.bayName} />
          <DisplayDetail label="Warehouse" value={detail.warehouseRef} />
          <DisplayDetail label="Dock" value={detail.dockName} />
          <DisplayDetail label="Dwell Hours" value={detail.dwellTime} />
          <DisplayDetail label="Appointment Number" value={detail.appointmentNumber} />
          <DisplayDetail label="Gate Out Time" value={formatDateTimeDisplay(detail.checkedOutAt)} />
        </div>
      </ExpandableHeader>

      {/* Section 5 — Documents & History */}
      <ExpandableHeader headerSection="Documents & History" headerType="primary">
        <div className="p-fluid-3 bg-[#F9FAFE] dark:bg-acs-dark-surface space-y-fluid-3">
          <div className="flex flex-wrap gap-fluid-4">
            {detail.sealPhotoUrl ? (
              <a
                href={detail.sealPhotoUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-fluid-sm font-medium text-[#1e2a74] underline"
              >
                <ExternalLink size={14} /> Seal photo
              </a>
            ) : (
              <span className="text-fluid-sm text-gray-400 dark:text-acs-dark-subtext">No seal photo on file</span>
            )}
            {checkInDriverEntry?.licensePhotoUrl ? (
              <a
                href={checkInDriverEntry.licensePhotoUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-fluid-sm font-medium text-[#1e2a74] underline"
              >
                <ExternalLink size={14} /> Driver license image
              </a>
            ) : (
              <span className="text-fluid-sm text-gray-400 dark:text-acs-dark-subtext">No driver license image on file</span>
            )}
          </div>

          <div>
            <p className="text-[11px] font-semibold text-gray-500 dark:text-acs-dark-subtext uppercase tracking-wide mb-fluid-1.5">
              Inspection Reports
            </p>
            {isLoadingInspections ? (
              <p className="text-fluid-sm text-gray-400 dark:text-acs-dark-subtext">Loading…</p>
            ) : inspections.length === 0 ? (
              <p className="text-fluid-sm text-gray-400 dark:text-acs-dark-subtext">No inspections recorded yet.</p>
            ) : (
              <div className="space-y-fluid-1.5">
                {inspections.map((insp) => {
                  const isHazardous = insp.outcome === "REJECTED" && HAZARDOUS_REJECTION_CODES.has(insp.rejectionCode);
                  return (
                    <div
                      key={insp.id}
                      className={`rounded-md border px-fluid-2 py-fluid-1.5 text-fluid-sm ${
                        isHazardous
                          ? "border-orange-400 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 ring-1 ring-orange-300 dark:ring-orange-800"
                          : insp.outcome === "REJECTED"
                            ? "border-red-300 dark:border-red-900/50 bg-red-50 dark:bg-red-900/10"
                            : insp.outcome === "MINOR_ISSUE"
                              ? "border-amber-300 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/10"
                              : "border-gray-200 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-fluid-2">
                        <span className="font-semibold text-gray-900 dark:text-acs-dark-text flex items-center gap-fluid-1">
                          {isHazardous && <Bug size={14} className="text-orange-700 dark:text-orange-400" />}
                          {CHECKPOINT_DISPLAY[insp.checkpointType] || insp.checkpointType} — {OUTCOME_DISPLAY[insp.outcome] || insp.outcome}
                        </span>
                        <span className="text-fluid-xs text-gray-400 dark:text-acs-dark-subtext whitespace-nowrap">
                          {formatDateTimeDisplay(insp.performedAt)}
                        </span>
                      </div>
                      {insp.outcome === "REJECTED" && (
                        <p className={`text-fluid-xs mt-fluid-0.5 ${isHazardous ? "text-orange-800 dark:text-orange-300 font-semibold" : "text-red-700 dark:text-red-400"}`}>
                          {insp.rejectionCode === "OTHER"
                            ? insp.rejectionCodeOther
                            : REJECTION_CODE_DISPLAY[insp.rejectionCode] || insp.rejectionCode}
                        </p>
                      )}
                      {insp.notes && (
                        <p className="text-fluid-xs text-gray-500 dark:text-acs-dark-subtext mt-fluid-0.5">{insp.notes}</p>
                      )}
                      <p className="text-fluid-xs text-gray-400 dark:text-acs-dark-subtext mt-fluid-0.5">
                        by {insp.performedByEmail}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </ExpandableHeader>
      </div>

      {/* Activity panel — mirrors ShipmentActivity's ActivityDrawer (same timeline/diff pattern),
          adapted to trailer events (check-in, moves, condition changes, loading/unloading
          actuals, checkout) instead of shipment field updates. */}
      {showActivityPanel && (
        <div className="fixed inset-0 z-[999]">
          <div
            className="absolute inset-0 bg-slate-900/30"
            onClick={() => setShowActivityPanel(false)}
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-[600px]">
            <div className="flex h-full flex-col overflow-hidden bg-white shadow-2xl ring-1 ring-black/5 dark:bg-acs-dark-surface dark:ring-acs-dark-border">
              <header className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gradient-to-r from-acs-primary-navy to-acs-primary-blue px-fluid-3 py-fluid-2 text-white dark:border-acs-dark-border">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10">
                    <Activity size={20} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-white/70">Trailer Timeline</p>
                    <p className="text-fluid-lg font-semibold">Activity Log</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowActivityPanel(false)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60"
                >
                  <X size={18} />
                </button>
              </header>

              <div className="flex-1 overflow-y-auto bg-gray-50/60 p-fluid-3 font-roboto text-fluid-base dark:bg-acs-dark-bg">
                {activityEntries.length > 0 ? (
                  <ol className="space-y-fluid-1.5">
                    {activityEntries.map((item, idx) => {
                      const formattedDate = formatDateTimeDisplay(item.movedAt) || "—";
                      const isLast = idx === activityEntries.length - 1;
                      return (
                        <li key={item.historyId ?? idx} className="relative pl-9">
                          {!isLast && (
                            <span className="absolute left-4 top-6 h-full w-px bg-gradient-to-b from-acs-secondary-mint/70 via-acs-primary-blue/30 to-transparent" />
                          )}
                          <span className="absolute left-1.5 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-white ring-4 ring-acs-secondary-mint shadow-sm dark:bg-acs-dark-surface dark:ring-acs-primary-blue/40">
                            <span className="h-2 w-2 rounded-full bg-acs-primary-blue" />
                          </span>

                          <div className="rounded-xl border border-gray-200 bg-white p-fluid-1.5 shadow-sm dark:border-acs-dark-border dark:bg-acs-dark-surface">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${EVENT_TYPE_BADGE_CLASS[item.eventType] || EVENT_TYPE_BADGE_CLASS.DEFAULT}`}>
                                  <Activity size={14} />
                                  {EVENT_TYPE_DISPLAY[item.eventType] || item.eventType || "Update"}
                                </span>
                                {item.fieldName ? (
                                  <span className="rounded-full bg-acs-secondary-mint/70 px-2 py-1 text-xs text-acs-primary-navy dark:bg-acs-primary-blue/25 dark:text-acs-dark-text">
                                    {item.fieldName}
                                  </span>
                                ) : null}
                              </div>
                              <span className="inline-flex items-center gap-1 text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
                                <Clock3 size={14} />
                                {formattedDate}
                              </span>
                            </div>

                            <div className="mt-1 flex flex-wrap items-center gap-2 text-fluid-sm text-gray-600 dark:text-acs-dark-subtext">
                              <User size={14} />
                              <span className="font-medium text-gray-800 dark:text-acs-dark-text">
                                {item.performedByName || "System"}
                              </span>
                              <span className="text-gray-400 dark:text-acs-dark-subtext">•</span>
                              <span>Trailer #{detail.trailerNumber || "—"}</span>
                            </div>

                            {(item.prevValue != null || item.newValue != null) && (
                              <div className="mt-fluid-1.5 grid grid-cols-1 gap-fluid-1.5 md:grid-cols-2">
                                <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-fluid-2 shadow-sm dark:border-acs-dark-border dark:from-acs-dark-surface dark:to-acs-dark-bg">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-acs-dark-subtext">Previous</p>
                                  <div className="mt-1 text-fluid-sm text-gray-800 dark:text-acs-dark-text break-words">
                                    {item.prevValue ?? <span className="text-gray-400 dark:text-acs-dark-subtext">—</span>}
                                  </div>
                                </div>
                                <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-fluid-2 shadow-sm dark:border-acs-dark-border dark:from-acs-dark-surface dark:to-acs-dark-bg">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-acs-dark-subtext">New</p>
                                  <div className="mt-1 text-fluid-sm text-gray-800 dark:text-acs-dark-text break-words">
                                    {item.newValue ?? <span className="text-gray-400 dark:text-acs-dark-subtext">—</span>}
                                  </div>
                                </div>
                              </div>
                            )}

                            {item.notes ? (
                              <div className="mt-fluid-1.5 inline-flex items-start gap-2 rounded-lg bg-acs-secondary-mint/80 px-3 py-2 text-fluid-sm text-acs-primary-navy dark:bg-acs-primary-blue/25 dark:text-acs-dark-text">
                                <MessageSquare size={14} />
                                <span className="leading-snug">{item.notes}</span>
                              </div>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white/70 p-fluid-4 text-fluid-sm text-gray-600 dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-subtext">
                    No activity logged yet. Updates will appear here as a clean timeline.
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Move Modal */}
      {showMoveModal && detail && (
        <MoveTrailerModal
          trailer={detail}
          warehouseRef={detail.warehouseRef}
          currentStatus={status}
          onClose={() => setShowMoveModal(false)}
          onSuccess={() => {
            fetchTrailerById(detail.id).then((data) => {
              setDetail(data);
              setStatus(STATUS_DISPLAY[data.status] || data.status || "");
            }).catch(() => {});
            onMoveSuccess?.();
          }}
        />
      )}

      {/* Record Inspection Modal */}
      {showInspectionModal && detail && (
        <RecordInspectionModal
          trailerId={detail.id}
          onClose={() => setShowInspectionModal(false)}
          onSuccess={() => {
            fetchTrailerById(detail.id).then((data) => {
              setDetail(data);
              setStatus(STATUS_DISPLAY[data.status] || data.status || "");
            }).catch(() => {});
            loadInspections(detail.id);
            onMoveSuccess?.();
          }}
        />
      )}

      {/* Clear Hold Modal */}
      {showClearHoldModal && detail && (
        <ClearHoldModal
          trailerId={detail.id}
          onClose={() => setShowClearHoldModal(false)}
          onSuccess={() => {
            fetchTrailerById(detail.id).then((data) => {
              setDetail(data);
              setStatus(STATUS_DISPLAY[data.status] || data.status || "");
            }).catch(() => {});
            onMoveSuccess?.();
          }}
        />
      )}

      {showRecordGateEventModal === "PICKUP_ARRIVAL" && detail && (
        <RecordGateEventModal
          title="Record Pickup Arrival"
          description="The pickup driver has arrived at the gate. This syncs to the shipment's gate-in actual."
          onClose={() => setShowRecordGateEventModal(null)}
          onConfirm={handleConfirmPickupArrival}
        />
      )}

      {showRecordGateEventModal === "DROPOFF_DEPARTURE" && detail && (
        <RecordGateEventModal
          title="Record Drop-off Departure"
          description="The drop-off driver is leaving the gate bobtail. This syncs to the shipment's gate-out actual."
          onClose={() => setShowRecordGateEventModal(null)}
          onConfirm={handleConfirmDropoffDeparture}
        />
      )}
    </div>
  );
};

export default TrailerDetailView;
